import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Chip, 
  Skeleton,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getLanguageByCode } from '../../constants';
import { formatDistanceToNow, parseISO } from 'date-fns';

const MyRecentVideos = ({ isLoggedIn, userId, history, checkAndExecute }) => {
  const { t, i18n } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // 전달받은 history 데이터에서 최근 8개 영상 필터링
  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // history prop에서 active 상태인 최근 8개 영상만 필터링
      const recentVideos = history
        .filter(item => item.status === 'active' && item.videoId && item.title)
        .slice(0, 8) // 최근 8개만
        .map(item => ({
          id: item.id,
          videoId: item.videoId,
          title: item.customTitle || item.title,
          youtubeTitle: item.youtubeTitle || item.title,
          url: item.url,
          summary: item.summary || '',
          createdAt: item.createdAt,
          language: item.language,
          thumbnail: `https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`
        }));
      
      setVideos(recentVideos);
    } catch (err) {
      console.error('최근 분석 영상 처리 에러:', err);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, userId, history]);

  // 영상 클릭 처리
  const handleVideoClick = async (videoId) => {
    // 기능 차단 체크 (유예 기간 만료 시 모달 표시)
    if (checkAndExecute) {
      const canExecute = checkAndExecute(() => performVideoClick(videoId));
      if (!canExecute) return;
    } else {
      await performVideoClick(videoId);
    }
  };

  // 실제 영상 클릭 로직
  const performVideoClick = async (videoId) => {
    const targetVideo = videos.find(v => v.videoId === videoId);
    if (targetVideo && targetVideo.id) {
      // 🌍 최근 분석 영상의 언어 정보 확인 및 자동 설정
      const videoLanguage = targetVideo.language || 'ko'; // 기본값: 한국어
      const currentLanguage = i18n.language;
      
      console.log(`🌍 [최근 분석 클릭] 언어 확인: 현재=${currentLanguage}, 영상=${videoLanguage}`);
      
      // 언어가 다른 경우 영상의 언어로 변경
      if (videoLanguage !== currentLanguage) {
        console.log(`🔄 [최근 분석 클릭] 언어 변경: ${currentLanguage} → ${videoLanguage}`);
        await i18n.changeLanguage(videoLanguage);
      }
      
      navigate(`/analyze/${targetVideo.id}`);
    }
  };

  // 제목 길이 제한
  const truncateTitle = (title, maxLength = 60) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  // 날짜 포맷팅
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Firestore Timestamp 객체인 경우
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return `1${t('relativeTime.dayAgo')}`;
      if (diffDays < 7) return `${diffDays}${t('relativeTime.daysAgo')}`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)}${t('relativeTime.weeksAgo')}`;
      return `${Math.ceil(diffDays / 30)}${t('relativeTime.monthsAgo')}`;
    } catch (error) {
      console.error('날짜 포맷팅 에러:', error);
      return '';
    }
  };

  // 로그인하지 않은 경우 렌더링하지 않음
  if (!isLoggedIn) {
    return null;
  }

  // 영상이 없는 경우
  if (!loading && videos.length === 0) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '800px' },
        mx: 'auto',
        mt: 4, 
        mb: 2,
        px: { xs: 2, sm: 3, md: 2 }
      }}
    >
      {/* 제목 */}
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 'bold',
          mb: 2,
          color: '#333',
          textAlign: 'left'
        }}
              >
          {t('landing.recentAnalysis')}
        </Typography>

      {/* 로딩 상태 */}
      {loading && (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Skeleton variant="rectangular" width="100%" height={120} />
                <CardContent sx={{ p: 1.5 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="60%" height={16} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 영상 그리드 */}
      {!loading && videos.length > 0 && (
        <Grid container spacing={2}>
          {videos.map((video) => (
            <Grid item xs={6} sm={4} md={3} key={video.id}>
              <Card 
                sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
                onClick={() => handleVideoClick(video.videoId)}
              >
                {/* 썸네일 */}
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height={isMobile ? 100 : 120}
                    image={video.thumbnail}
                    alt={video.title}
                    sx={{
                      objectFit: 'cover',
                      backgroundColor: '#f5f5f5'
                    }}
                  />
                  {/* 재생 아이콘 오버레이 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <PlayArrowIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                </Box>

                {/* 콘텐츠 */}
                <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
                  {/* 제목 */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      lineHeight: 1.3,
                      mb: 0.5,
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      color: '#333'
                    }}
                  >
                    {truncateTitle(video.title, isMobile ? 40 : 60)}
                  </Typography>

                  {/* 국기와 분석 날짜 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Box
                      sx={{
                        fontSize: '1.1em',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 20,
                        height: 20
                      }}
                    >
                      {getLanguageByCode(video.language || 'ko').flag}
                    </Box>
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                      label={formatDate(video.createdAt)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: '#f0f0f0',
                        color: '#666',
                        '& .MuiChip-icon': {
                          fontSize: '14px',
                          color: '#666'
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MyRecentVideos; 